// apps/djayma/src/pages/admin/AdminLogin.tsx

import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { ADMIN_THEME } from "@/lib/admin-theme";
import { DjaymaLogo } from "@/components/DjaymaLogo";
import { Loader2, AlertCircle } from "lucide-react";

// Dev bypass: DISABLED — admin auth must always be enforced.
// Configure VITE_SUPABASE_URL in .env.local for local development.
const DEV_BYPASS = false;
const MAX_ATTEMPTS = 3;
const COOLDOWN_SECONDS = 30;

export default function AdminLogin() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  // Redirect already-authenticated admin users
  // In DEV_BYPASS mode, skip redirect so the login page can be visually tested.
  // Add ?preview=true to force-show login in dev bypass: /admin/connexion?preview=true
  useEffect(() => {
    if (DEV_BYPASS && !searchParams.has("preview")) {
      navigate("/admin", { replace: true });
      return;
    }
    if (DEV_BYPASS) return;

    const checkExistingSession = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: adminUser } = await supabase
          .from("admin_users")
          .select("role, is_active, platform, department:departments(name)")
          .eq("id", user.id)
          .eq("is_active", true)
          .single();

        const hasDjaymaAccess = adminUser?.platform === "djayma" ||
          (adminUser?.department as { name?: string } | null)?.name === "Direction";
        if (adminUser?.is_active && ["admin", "manager"].includes(adminUser.role) && hasDjaymaAccess) {
          navigate(getValidReturnUrl(), { replace: true });
        }
      } catch {
        // Network error during session check — stay on login page
      }
    };

    checkExistingSession();
  }, [navigate, searchParams]);

  // Show session expired toast when redirected with ?reason=timeout
  useEffect(() => {
    if (searchParams.get("reason") === "timeout") {
      toast.warning("Votre session a expiré. Veuillez vous reconnecter.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const getValidReturnUrl = (): string => {
    const raw = searchParams.get("returnUrl");
    if (!raw) return "/admin";
    try {
      const decoded = decodeURIComponent(raw);
      const url = new URL(decoded, window.location.origin);
      if (
        url.origin === window.location.origin &&
        url.pathname.startsWith("/admin")
      ) {
        return url.pathname + url.search;
      }
    } catch {
      // malformed URI — fall through to default
    }
    return "/admin";
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) return;
    if (cooldown > 0) return;

    setLoading(true);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (authError || !data.session) {
        const newCount = failCount + 1;
        setFailCount(newCount);
        if (newCount >= MAX_ATTEMPTS) {
          setCooldown(COOLDOWN_SECONDS);
          setFailCount(0);
          setError(t("login.error_rate_limit"));
        } else {
          setError(t("login.error_invalid"));
        }
        setLoading(false);
        return;
      }

      // Verify admin role + platform (Direction department can access both platforms)
      const { data: adminUser, error: adminError } = await supabase
        .from("admin_users")
        .select("role, is_active, platform, department:departments(name)")
        .eq("id", data.user.id)
        .eq("is_active", true)
        .single();

      const isDjayma = adminUser?.platform === "djayma";
      const isDirection = (adminUser?.department as { name?: string } | null)?.name === "Direction";
      if (adminError || !adminUser?.is_active || !["admin", "manager"].includes(adminUser.role) || (!isDjayma && !isDirection)) {
        await supabase.auth.signOut();
        setError(t("login.error_unauthorized"));
        setLoading(false);
        return;
      }

      setLoading(false);
      navigate(getValidReturnUrl(), { replace: true });
      return;
    } catch {
      setError(t("login.error_network"));
      setLoading(false);
    }
  };

  const isDisabled = loading || cooldown > 0;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative"
      style={{
        background: `linear-gradient(135deg, ${ADMIN_THEME.login.bgFrom} 0%, ${ADMIN_THEME.login.bgTo} 100%)`,
      }}
    >
      {/* Geometric pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.06,
          backgroundImage:
            "repeating-linear-gradient(60deg, #FFF8F0 0px, #FFF8F0 1px, transparent 1px, transparent 20px), repeating-linear-gradient(-60deg, #FFF8F0 0px, #FFF8F0 1px, transparent 1px, transparent 20px)",
        }}
      />

      <div
        className="w-full max-w-[400px] rounded-2xl p-8 relative shadow-2xl"
        style={{ background: ADMIN_THEME.login.card }}
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <DjaymaLogo height={40} variant="light" />
          </div>
          <p className="text-xs uppercase tracking-widest text-stone-500">
            {t("login.subtitle")}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div
            className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg p-3 mb-4"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="admin-email" className="block text-sm font-medium text-stone-700">
              {t("login.email_label")}
            </label>
            <input
              id="admin-email"
              type="email"
              placeholder={t("login.email_placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={isDisabled}
              required
              className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta disabled:opacity-50"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="admin-password" className="block text-sm font-medium text-stone-700">
              {t("login.password_label")}
            </label>
            <input
              id="admin-password"
              type="password"
              placeholder={t("login.password_placeholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={isDisabled}
              required
              className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={isDisabled}
            className="w-full rounded-lg py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:ring-offset-2"
            style={{ background: ADMIN_THEME.login.button }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {t("login.submitting")}
              </span>
            ) : cooldown > 0 ? (
              `${t("login.error_rate_limit")} (${cooldown}s)`
            ) : (
              t("login.submit")
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-stone-400">
          {t("login.footer")}
        </p>
      </div>
    </div>
  );
}
